import React, { useEffect, useContext } from 'react';
import { ThemeProvider } from 'styled-components';
import { Spinner } from '../../../../components';
import { AppContext } from '../../contexts/AppContext';
import { BlockitThemeProvider, useBlockitThemeContext, AlluviumGlobalOverrides } from '../../../../themes';
import AntDesignStyleOverrides from './AntDesignStyleOverrides';
import { AppHeader } from './AppHeader'; // FIXME: everything under src/App should be core
import { useQuery } from '@apollo/client';
import { GetSimplifiedViewer } from '../../../../hooks/queries/useGetViewerOrganizationQuery';
import { useViewerQuery } from '../../../../hooks/queries/useViewerQuery';
import store from '../../../../localstore';

const LayoutInner = ({ children }) => {
  const ORGANIZATION_LIMIT = 1;
  const { currentLocation, currentOrganization, viewer, init } = useContext(AppContext);
  const { theme, isAlluvium } = useBlockitThemeContext();
  const baseUrl = isAlluvium ? 'https://www.alluviumhealth.com' : 'https://www.blockitnow.com';
  const { data: simplifiedViewer, loading: loadingSimplifiedViewer } = useQuery(GetSimplifiedViewer, {
    variables: { skip_locations: true },
  });
  // Read the persisted current org id directly (not currentOrganization from AppContext) - on
  // first load that context value is itself derived from this query's result, so it isn't
  // populated yet. Storage already is: it's the same id sent as the x-current-organization-id
  // header (graphqlclient.ts).
  const currentOrganizationId =
    window.sessionStorage.getItem('currentOrganizationId') || store().getItem('currentOrganizationId');
  const { data: viewerData, loading: loadingViewerData } = useViewerQuery({
    variables: { limit: ORGANIZATION_LIMIT, current_organization_id: currentOrganizationId || undefined },
  });

  useEffect(() => {
    if (simplifiedViewer && viewerData && !loadingSimplifiedViewer && !loadingViewerData) {
      const richOrganizationsById = new Map(viewerData.viewer.organizations.map(org => [org.id, org]));
      const newOrgs = simplifiedViewer.viewer.organizations.map(org => richOrganizationsById.get(org.id) ?? org);

      const updatedData = {
        viewer: {
          ...viewerData.viewer,
          organizations: newOrgs,
        },
      };
      init(updatedData.viewer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simplifiedViewer, viewerData, loadingSimplifiedViewer, loadingViewerData]);

  return (
    <ThemeProvider theme={theme}>
      {isAlluvium && <AlluviumGlobalOverrides />}
      <AntDesignStyleOverrides>
        <div
          className={`layoutContainer${isAlluvium ? ' alluvium-theme' : ''}`}
          style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
        >
          <AppHeader />
          {currentLocation && currentOrganization && viewer ? (
            <div className={`flex justify-center font-source-sans-pro`} style={{ flex: 1, minHeight: '86vh' }}>
              <div className={`${isAlluvium ? 'pt-8 pb-4' : 'py-4'} w-full max-w-3xl min-h-100-vh flex flex-col`}>
                <div>{children}</div>
                <div className={`${isAlluvium ? 'pt-8' : 'px-4 pt-4'} w-full text-grey-dark text-xs text-center`}>
                  <span>
                    <a className="text-grey-dark" style={{ color: 'inherit' }} href={`${baseUrl}/terms`}>
                      Terms of Service
                    </a>
                    &nbsp;&nbsp;|&nbsp;&nbsp;
                    <a className="text-grey-dark" style={{ color: 'inherit' }} href={`${baseUrl}/use`}>
                      Acceptable Use Policy
                    </a>
                    &nbsp;&nbsp;|&nbsp;&nbsp;
                    <a className="text-grey-dark" style={{ color: 'inherit' }} href={`${baseUrl}/privacy`}>
                      Privacy Policy
                    </a>
                    &nbsp;&nbsp;© 2018-{new Date().getFullYear()} {isAlluvium ? 'Alluvium Health' : 'Blockit, Inc.'}.
                    All Rights Reserved.{' '}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <Spinner />
          )}
        </div>
      </AntDesignStyleOverrides>
    </ThemeProvider>
  );
};

const Layout = ({ children }) => (
  <BlockitThemeProvider>
    <LayoutInner>{children}</LayoutInner>
  </BlockitThemeProvider>
);

// needed because the All Locations location of every organization has
// a null id, therefore sends and receives fields go off of whatever is in the
// cache, so we need to update the sends and receives fields for each
// All Location based on the other locations

export const updateSendsAndReceivesForAllLocationsLocation = ({ viewer }) => {
  return {
    ...viewer,
    organizations: [
      ...viewer.organizations.map(org => {
        if (!org.locations) {
          return org;
        }
        const allLocations = org.locations[0];
        const otherLocations = org.locations.slice(1, org.locations.length);
        const sendsAndReceives = otherLocations.reduce((acc, loc) => {
          return {
            sends: acc.sends || loc.sends,
            receives: acc.receives || loc.receives,
          };
        }, {});

        const updateLocations = [{ ...allLocations, ...sendsAndReceives }, ...otherLocations];
        return { ...org, locations: [...updateLocations] };
      }),
    ],
  };
};

export default Layout;
